import React from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { featureFlags } from '../../config/featureFlags'
import { GovernanceListingSection } from '../../components/governance/GovernanceListingSection'
import ExecutiveStandardDrilldown from '../../components/executive-governance/ExecutiveStandardDrilldown'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import { ComplianceCenterMetricTile } from '../../components/compliance-center/ComplianceCenterMetricTile'
import { Button } from '../../components/ui/Button'
import {
  fetchExecutiveOverview,
  recomputeExecutiveGovernance,
  executiveGovernanceStandardPath,
  type ExecutivePosture,
} from '../../services/executiveGovernanceApi'
import { useNavigate } from 'react-router-dom'

type Tab = 'posture' | 'stability' | 'trajectory' | 'instability' | 'pressure' | 'blast'

export default function ComplianceCenterExecutiveGovernanceWorkspace() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { standardCode: routeStandard } = useParams<{ standardCode?: string }>()
  const decodedStandard = routeStandard ? decodeURIComponent(routeStandard) : null

  const [tab, setTab] = React.useState<Tab>('posture')
  const [loading, setLoading] = React.useState(true)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [posture, setPosture] = React.useState<ExecutivePosture | null>(null)
  const [recomputeMeta, setRecomputeMeta] = React.useState<{
    snapshotInserted?: boolean
    dedupReason?: string
  } | null>(null)

  const load = React.useCallback(async () => {
    if (decodedStandard) return
    setLoading(true)
    setError(null)
    try {
      const data = await fetchExecutiveOverview()
      setPosture(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load executive governance')
    } finally {
      setLoading(false)
    }
  }, [decodedStandard])

  React.useEffect(() => {
    void load()
  }, [load])

  const handleRecompute = async () => {
    setBusy(true)
    try {
      const result = await recomputeExecutiveGovernance()
      setRecomputeMeta({
        snapshotInserted: result.snapshotInserted ?? result.diagnostics?.snapshotInserted,
        dedupReason: result.dedupReason ?? result.diagnostics?.dedupReason ?? undefined,
      })
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Recompute failed')
    } finally {
      setBusy(false)
    }
  }

  if (!featureFlags.EXECUTIVE_GOVERNANCE_V1) {
    return <Navigate to="/compliance-center/overview" replace />
  }

  if (decodedStandard) {
    return <ExecutiveStandardDrilldown standardCode={decodedStandard} />
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'posture', label: t('executiveGovernance.executiveOperationalPosture', 'Executive Operational Posture') },
    { id: 'stability', label: t('executiveGovernance.certificationStability', 'Certification Stability') },
    { id: 'trajectory', label: t('executiveGovernance.readinessTrajectory', 'Readiness Trajectory') },
    { id: 'instability', label: t('executiveGovernance.governanceInstability', 'Governance Instability') },
    { id: 'pressure', label: t('executiveGovernance.operationalPressure', 'Operational Pressure') },
    { id: 'blast', label: t('executiveGovernance.blastRadius', 'Blast Radius') },
  ]

  const mostDegraded = posture?.pressure?.mostDegradedStandards

  return (
    <div className={listStyles.pageStack}>
      <GovernanceListingSection
        title={t('executiveGovernance.executiveGovernance', 'Executive Governance')}
        description={t(
          'executiveGovernance.workspaceIntro',
          'Deterministic CISO oversight from operational assurance, runtime events, blockers, and shared controls — scoped to your visibility.',
        )}
        toolbar={
          <Button type="button" color="brand" size="small" disabled={busy} onClick={() => void handleRecompute()}>
            {t('executiveGovernance.recompute', 'Recompute')}
          </Button>
        }
      >
        {error ? <p className="text-sm text-red-700">{error}</p> : null}
        {loading ? <p className="text-sm text-gray-500">{t('common.loading', 'Loading…')}</p> : null}

        {posture?.lastSnapshotGeneratedAt ? (
          <p className="text-xs text-slate-500 m-0">
            {t('executiveGovernance.lastExecutiveRecompute', 'Last executive recompute')}:{' '}
            {new Date(posture.lastSnapshotGeneratedAt).toLocaleString()}
          </p>
        ) : null}

        {recomputeMeta?.snapshotInserted === false && recomputeMeta.dedupReason ? (
          <p className="text-xs text-amber-700 m-0">
            {t('executiveGovernance.snapshotUnchanged', 'Snapshot unchanged')} ({recomputeMeta.dedupReason})
          </p>
        ) : null}

        <div className={listStyles.segmentedTabs} role="tablist">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              className={`${listStyles.segmentedTab} ${tab === item.id ? listStyles.segmentedTabActive : ''}`}
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>

        {tab === 'posture' && posture ? (
          <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid4}`}>
            <ComplianceCenterMetricTile
              label={t('executiveGovernance.certificationRisk', 'Certification Risk')}
              value={posture.certificationRiskLevel}
              tone="red"
            />
            <ComplianceCenterMetricTile
              label={t('executiveGovernance.readinessTrajectory', 'Readiness Trajectory')}
              value={posture.readinessTrajectory}
              tone="blue"
            />
            <ComplianceCenterMetricTile
              label={t('executiveGovernance.unstableWorkflow', 'Unstable Workflow')}
              value={posture.unstableWorkflowCount ?? 0}
              tone="orange"
            />
            <ComplianceCenterMetricTile
              label={t('executiveGovernance.governanceRecovery', 'Governance Recovery')}
              value={`${posture.governanceStability ?? 0}%`}
              tone="green"
            />
          </div>
        ) : null}

        {mostDegraded?.length ? (
          <div className="mt-3">
            <p className="text-xs font-medium text-slate-700 m-0 mb-2">
              {t('executiveGovernance.executiveDrilldown', 'Executive drill-down')}
            </p>
            <div className="flex flex-wrap gap-2">
              {mostDegraded.slice(0, 6).map((s) => (
                <Button
                  key={s.standardCode}
                  type="button"
                  size="small"
                  color="outline"
                  onClick={() => navigate(executiveGovernanceStandardPath(s.standardCode))}
                >
                  {s.standardCode}
                </Button>
              ))}
            </div>
          </div>
        ) : null}

        {tab === 'stability' && posture?.stability ? (
          <ul className="text-sm space-y-2 m-0 p-0 list-none">
            <li>
              {t('executiveGovernance.certificationStability', 'Certification Stability')}:{' '}
              <strong>{posture.stability.level}</strong> (score {posture.stability.score})
            </li>
            {(posture.stability.factors || []).map((f) => (
              <li key={f.code} className="text-xs text-gray-600">
                {f.code}: {f.count}
              </li>
            ))}
          </ul>
        ) : null}

        {tab === 'trajectory' && posture?.trajectory ? (
          <p className="text-sm m-0">
            {posture.trajectory.trajectory} — Δ {posture.trajectory.deltaScore ?? 0}
          </p>
        ) : null}

        {tab === 'instability' && posture?.instability ? (
          <ul className="text-sm m-0 p-0 list-none space-y-1">
            <li>
              Index: {posture.instability.instabilityIndex} ({posture.instability.band})
            </li>
            {(posture.instability.signals || []).map((s) => (
              <li key={s.code} className="text-xs text-gray-600">
                {s.code}: {s.count}
              </li>
            ))}
          </ul>
        ) : null}

        {tab === 'pressure' && posture ? (
          <ul className="text-sm m-0 p-0 list-none space-y-1">
            <li>
              {t('executiveGovernance.operationalPressure', 'Operational Pressure')}: {posture.overallOperationalPressure}
            </li>
            <li>
              {t('executiveGovernance.degradedControls', 'Degraded controls')}: {posture.degradedControlCount}
            </li>
          </ul>
        ) : null}

        {tab === 'blast' ? (
          <ul className="text-sm m-0 p-0 list-none space-y-2">
            {(posture?.blastRadius?.impacts || []).length === 0 ? (
              <li className="text-gray-500 italic">{t('executiveGovernance.noBlastRadius', 'No shared degraded controls in scope.')}</li>
            ) : (
              posture?.blastRadius?.impacts?.map((impact) => (
                <li key={impact.controlCode} className={listStyles.listRow}>
                  {impact.summary}
                </li>
              ))
            )}
          </ul>
        ) : null}

        {posture?.explanations?.lines?.length ? (
          <div className="mt-4">
            <h4 className="text-sm font-medium">{t('executiveGovernance.explainability', 'Explainability')}</h4>
            <ul className="text-xs text-gray-600 list-disc pl-4 mt-1">
              {posture.explanations.lines.map((line) => (
                <li key={line.code}>{line.message}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </GovernanceListingSection>
    </div>
  )
}
